import { CalculatorInputs, PricingConfig } from '@/types/calculator';

/**
 * Interface for shareable calculator state
 */
export interface ShareableState {
  inputs: CalculatorInputs;
  currency: string;
  calculatorMode: 'wizard' | 'detailed' | 'simple';
  customName?: string;
  pricing?: PricingConfig;
  version: string; // For future compatibility
  createdAt?: string; // ISO timestamp when the calculation was created
}

/**
 * Creates a shareable URL from calculator state using server-side encryption
 */
export async function createShareUrl(
  inputs: CalculatorInputs,
  currency: string,
  calculatorMode: 'wizard' | 'detailed' | 'simple',
  customName?: string,
  pricing?: PricingConfig
): Promise<string> {
  console.log('🚀 createShareUrl called with customName:', customName);
  console.log('🔍 INPUT VALIDATION:');
  console.log('  - inputs.staffTurnoverRate:', inputs.staffTurnoverRate);
  console.log('  - inputs.industry:', inputs.industry);
  console.log('  - Full inputs received:', JSON.stringify(inputs, null, 2));
  
  const shareData: ShareableState = {
    inputs,
    currency,
    calculatorMode,
    customName,
    pricing,
    version: '2.0',
    createdAt: new Date().toISOString()
  };
    
  try {
    const response = await fetch('/api/share/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ shareData }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const { shareId } = await response.json();
    
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const shareUrl = `${baseUrl}/share/${shareId}`;
    
    return shareUrl;
  } catch (error) {
    console.error('Failed to create share URL:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to create shareable link');
  }
}

/**
 * Decodes a share URL parameter back to calculator state using server-side decryption
 */
export async function decodeShareData(shareParam: string): Promise<ShareableState> {
  try {
    const response = await fetch(`/api/share/${shareParam}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Invalid or corrupted share link');
    }

    const { shareData } = await response.json();
    
    // Validate the decoded data structure
    if (!shareData.inputs || !shareData.currency || !shareData.calculatorMode) {
      throw new Error('Invalid share data structure');
    }
    
    return shareData;
  } catch (error) {
    console.error('Failed to decode share data:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Invalid or corrupted share link');
  }
}